
import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductInfo } from '../../../mocks/products';

interface ZoomModalProps {
  images: string[];
  initialIndex: number;
  productName: string;
  onClose: () => void;
  accentColor: string;
}

function ZoomModal({ images, initialIndex, productName, onClose, accentColor }: ZoomModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const positionRef = useRef({ x: 0, y: 0 });

  const handleZoomIn = () => setScale(s => Math.min(s + 0.5, 4));
  const handleZoomOut = () => {
    setScale(s => {
      const next = Math.max(s - 0.5, 1);
      if (next === 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  };
  const handleReset = () => { setScale(1); setPosition({ x: 0, y: 0 }); };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scale <= 1) return;
    setIsDragging(true);
    dragStart.current = { x: e.clientX - positionRef.current.x, y: e.clientY - positionRef.current.y };
  }, [scale]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    const newPos = { x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y };
    positionRef.current = newPos;
    setPosition(newPos);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) handleZoomIn();
    else handleZoomOut();
  };

  const switchImage = (idx: number) => {
    setCurrentIndex(idx);
    handleReset();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/90" onClick={(e) => e.target === e.currentTarget && onClose()}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0">
        <span className="text-white/70 text-sm font-medium">{currentIndex + 1} / {images.length}</span>
        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomOut}
            disabled={scale <= 1}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all disabled:opacity-30 cursor-pointer"
          >
            <i className="ri-zoom-out-line text-lg"></i>
          </button>
          <span className="text-white/70 text-sm w-12 text-center">{Math.round(scale * 100)}%</span>
          <button
            onClick={handleZoomIn}
            disabled={scale >= 4}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all disabled:opacity-30 cursor-pointer"
          >
            <i className="ri-zoom-in-line text-lg"></i>
          </button>
          <button
            onClick={handleReset}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer ml-1"
          >
            <i className="ri-refresh-line text-lg"></i>
          </button>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all cursor-pointer ml-2"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
      </div>

      {/* Main image area */}
      <div
        className="flex-1 overflow-hidden relative flex items-center justify-center"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        style={{ cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in' }}
        onClick={() => { if (!isDragging && scale === 1) handleZoomIn(); }}
      >
        <img
          src={images[currentIndex]}
          alt={productName}
          draggable={false}
          className="max-w-full max-h-full object-contain select-none transition-transform duration-200"
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
          }}
        />
      </div>

      {/* Thumbnails */}
      <div className="flex items-center justify-center gap-3 px-6 py-4 flex-shrink-0">
        {images.map((img, idx) => (
          <button
            key={idx}
            onClick={() => switchImage(idx)}
            className="w-16 h-16 rounded-lg overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0"
            style={{ borderColor: currentIndex === idx ? accentColor : 'transparent', opacity: currentIndex === idx ? 1 : 0.5 }}
          >
            <img src={img} alt={`${productName} ${idx + 1}`} className="w-full h-full object-contain bg-white" />
          </button>
        ))}
      </div>

      {/* Hint */}
      <p className="text-center text-white/30 text-xs pb-3">クリックまたはスクロールでズーム・ドラッグで移動</p>
    </div>
  );
}

export default function ProductHero({ product }: { product: ProductInfo }) {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);

  return (
    <>
      <section className="relative pt-24 pb-16 bg-gradient-to-br from-[#f5f3ef] via-white to-[#f5f3ef]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Images */}
            <div className="space-y-4">
              <div
                className="relative bg-white rounded-2xl shadow-lg overflow-hidden group cursor-zoom-in"
                style={{ width: '100%', height: '500px' }}
                onClick={() => setZoomOpen(true)}
              >
                <img
                  src={product.galleryImages[selectedImage]}
                  alt={product.fullName}
                  title={product.fullName}
                  className="w-full h-full object-contain object-center transition-transform duration-300 group-hover:scale-105"
                />
                {/* Zoom hint overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="bg-black/40 rounded-full px-4 py-2 flex items-center gap-2">
                    <i className="ri-zoom-in-line text-white text-lg"></i>
                    <span className="text-white text-sm font-medium">クリックでズーム</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {product.galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`relative bg-white rounded-lg overflow-hidden transition-all cursor-pointer ${
                      selectedImage === idx ? 'ring-2 ring-offset-2' : 'opacity-60 hover:opacity-100'
                    }`}
                    style={{
                      width: '100%',
                      height: '100px',
                      ringColor: selectedImage === idx ? product.color : 'transparent'
                    }}
                  >
                    <img
                      src={img}
                      alt={`${product.fullName} ${idx + 1}`}
                      title={`${product.fullName} ${idx + 1}`}
                      className="w-full h-full object-contain object-center"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Info */}
            <div className="space-y-6">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ backgroundColor: `${product.color}15` }}>
                  <span className="text-xs font-bold tracking-wider" style={{ color: product.color }}>{product.label}</span>
                </div>
                <h1 className="text-4xl font-bold text-[#1a2332] mb-3" style={{ fontFamily: 'Noto Serif JP, serif' }}>
                  {product.fullName}
                </h1>
                <p className="text-xl text-[#4a4a4a] font-medium mb-6">{product.tagline}</p>
                <p className="text-base text-[#4a4a4a] leading-relaxed">{product.description}</p>
              </div>

              <div className="border-t border-[#e8e6e3] pt-6">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-[#1a2332]">¥{product.price.toLocaleString()}</span>
                  <span className="text-sm text-[#4a4a4a]">{product.priceNote}</span>
                </div>
                <p className="text-sm text-[#4a4a4a] mb-6">対象: {product.targetUser}</p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="#cta"
                    className="flex-1 text-center px-8 py-4 rounded-full text-base font-bold text-white transition-all whitespace-nowrap cursor-pointer"
                    style={{ backgroundColor: product.color }}
                  >
                    お問い合わせ
                  </a>
                  <a
                    href="/compare"
                    onClick={(e) => { e.preventDefault(); window.REACT_APP_NAVIGATE('/compare'); window.scrollTo(0, 0); }}
                    className="flex-1 text-center px-8 py-4 rounded-full text-base font-bold border-2 transition-all whitespace-nowrap cursor-pointer"
                    style={{ borderColor: product.color, color: product.color }}
                  >
                    他モデルと比較
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {zoomOpen && (
        <ZoomModal
          images={product.galleryImages}
          initialIndex={selectedImage}
          productName={product.fullName}
          onClose={() => setZoomOpen(false)}
          accentColor={product.color}
        />
      )}
    </>
  );
}
